/**
 * Self-test suite for missed-call notifications + history.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/MissedCallSelfTest.swift`.
 *
 * ## Test sections
 *
 *   A. Event value type (caller display hint derivation)
 *   B. Store (IndexedDB — callId dedup, unread counter, per-thread isolation)
 *   C. Notification policy (rate-limit, mute, non-roster body)
 *   D. Persistence (write → reopen DB → state preserved)
 *
 * All store-touching cases use isolated IDB databases (unique names per run)
 * so the suite is safe to execute on every dev launch.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * Auto-discovered by Vite `import.meta.glob` in `selftest/index.ts`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  makeMissedCallEvent,
  UNKNOWN_CALLER_HINT,
} from "./MissedCallEvent";
import type { RosterLookup } from "./MissedCallEvent";
import { MissedCallStore } from "./MissedCallStore";
import {
  shouldNotify,
  EMPTY_WINDOW,
  UNKNOWN_CALLER_BODY,
  RATE_LIMIT_MAX,
  RATE_LIMIT_WINDOW_MS,
} from "./MissedCallNotificationPolicy";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

/** Returns a fresh isolated MissedCallStore for each test case. */
function freshStore(): MissedCallStore {
  dbCounter += 1;
  return new MissedCallStore(
    `kaether:missed-calls:selftest:${dbCounter}:${Date.now()}`,
  );
}

/** Roster with "alice" → "Alice" and nothing else. */
const rosterWithAlice: RosterLookup = {
  displayName(userId: string): string | undefined {
    if (userId === "alice") return "Alice";
    return undefined;
  },
};

/** Empty roster — no one is known. */
const emptyRoster: RosterLookup = {
  displayName(): string | undefined {
    return undefined;
  },
};

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Missed call (notifications + history)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // ── A. Event value type ─────────────────────────────────────────────

        // A1: server-supplied name in payload is ignored; only local roster is used
        (async (): Promise<TestCase> => {
          const name = "Event: server-supplied from_name is discarded; roster name is used";
          try {
            const event = makeMissedCallEvent(
              "call-a1",
              "thread-1",
              "alice",
              rosterWithAlice,
              1000,
              // server sends a forged display name — should be ignored
              { from_name: "EVIL SERVER NAME", from_id: "alice" },
            );
            if (event.callerDisplayHint !== "Alice") {
              return fail(
                name,
                `Expected "Alice" from roster, got "${event.callerDisplayHint}"`,
              );
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // A2: non-roster peer → display hint = UNKNOWN_CALLER_HINT
        (async (): Promise<TestCase> => {
          const name = "Event: non-roster peer → callerDisplayHint is UNKNOWN_CALLER_HINT";
          try {
            const event = makeMissedCallEvent(
              "call-a2",
              "thread-1",
              "unknown-user-xyz",
              emptyRoster,
              1000,
            );
            if (event.callerDisplayHint !== UNKNOWN_CALLER_HINT) {
              return fail(
                name,
                `Expected "${UNKNOWN_CALLER_HINT}", got "${event.callerDisplayHint}"`,
              );
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // ── B. Store ────────────────────────────────────────────────────────

        // B1: log missed call → unread counter increments
        (async (): Promise<TestCase> => {
          const name = "Store: log missed call → unread counter increments";
          try {
            const store = freshStore();
            const event = makeMissedCallEvent(
              "call-b1",
              "thread-b1",
              "alice",
              rosterWithAlice,
            );
            const before = await store.unreadCount("thread-b1");
            const result = await store.log(event);
            const after = await store.unreadCount("thread-b1");

            if (!result.inserted) {
              return fail(name, "Expected inserted=true for first log");
            }
            if (before !== 0) {
              return fail(name, `Expected 0 unread before log, got ${before}`);
            }
            if (after !== 1) {
              return fail(name, `Expected 1 unread after log, got ${after}`);
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // B2: callId dedup — same callId twice = single entry
        (async (): Promise<TestCase> => {
          const name = "Store: callId dedup — same callId logged twice = single entry";
          try {
            const store = freshStore();
            const event = makeMissedCallEvent(
              "call-b2",
              "thread-b2",
              "alice",
              rosterWithAlice,
            );
            const r1 = await store.log(event);
            const r2 = await store.log(event); // duplicate

            if (!r1.inserted) {
              return fail(name, "First log should have inserted=true");
            }
            if (r2.inserted) {
              return fail(name, "Duplicate log should have inserted=false");
            }
            const history = await store.history("thread-b2");
            if (history.length !== 1) {
              return fail(
                name,
                `Expected 1 entry after dedup, got ${history.length}`,
              );
            }
            const unread = await store.unreadCount("thread-b2");
            if (unread !== 1) {
              return fail(
                name,
                `Expected unread=1 after dedup, got ${unread}`,
              );
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // B3: per-thread isolation
        (async (): Promise<TestCase> => {
          const name = "Store: per-thread isolation";
          try {
            const store = freshStore();
            const e1 = makeMissedCallEvent(
              "call-iso-1",
              "thread-iso-A",
              "alice",
              rosterWithAlice,
            );
            const e2 = makeMissedCallEvent(
              "call-iso-2",
              "thread-iso-B",
              "alice",
              rosterWithAlice,
            );
            await store.log(e1);
            await store.log(e2);

            const histA = await store.history("thread-iso-A");
            const histB = await store.history("thread-iso-B");

            if (histA.length !== 1) {
              return fail(name, `thread-iso-A expected 1 entry, got ${histA.length}`);
            }
            if (histB.length !== 1) {
              return fail(name, `thread-iso-B expected 1 entry, got ${histB.length}`);
            }
            if (histA[0]!.callId !== "call-iso-1") {
              return fail(name, `thread-iso-A has wrong callId: ${histA[0]!.callId}`);
            }
            if (histB[0]!.callId !== "call-iso-2") {
              return fail(name, `thread-iso-B has wrong callId: ${histB[0]!.callId}`);
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // ── C. Notification policy ──────────────────────────────────────────

        // C1: 5 missed calls in 4 min → all notified
        (async (): Promise<TestCase> => {
          const name = "Policy: 5 missed calls within window → all notified";
          try {
            const baseMs = Date.now();
            let window = EMPTY_WINDOW;
            let allNotified = true;

            for (let i = 0; i < RATE_LIMIT_MAX; i++) {
              const decision = shouldNotify(
                "thread-c1",
                "Alice",
                false,
                baseMs + i * 30_000, // 30s apart → well within 5-min window
                window,
              );
              if (decision.tag !== "notify") {
                allNotified = false;
                break;
              }
              window = decision.nextWindow;
            }

            if (!allNotified) {
              return fail(name, `One of the first ${RATE_LIMIT_MAX} calls was suppressed`);
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // C2: 6th missed call in 5 min → suppressed (rate-limit)
        (async (): Promise<TestCase> => {
          const name = `Policy: ${RATE_LIMIT_MAX + 1}th missed call in window → rate-limited`;
          try {
            const baseMs = Date.now();
            let window = EMPTY_WINDOW;

            // Consume all ${RATE_LIMIT_MAX} slots
            for (let i = 0; i < RATE_LIMIT_MAX; i++) {
              const decision = shouldNotify(
                "thread-c2",
                "Alice",
                false,
                baseMs + i * 1_000,
                window,
              );
              if (decision.tag !== "notify") {
                return fail(name, `Pre-fill call ${i + 1} was unexpectedly suppressed`);
              }
              window = decision.nextWindow;
            }

            // The (RATE_LIMIT_MAX + 1)th call within the same window
            const extra = shouldNotify(
              "thread-c2",
              "Alice",
              false,
              baseMs + RATE_LIMIT_MAX * 1_000, // still inside 5-min window
              window,
            );

            if (extra.tag !== "suppress") {
              return fail(
                name,
                `Expected suppress, got tag="${extra.tag}"`,
              );
            }
            if (extra.reason !== "rateLimit") {
              return fail(name, `Expected reason="rateLimit", got "${extra.reason}"`);
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // C3: muted thread → no notification (but store still has the entry)
        (async (): Promise<TestCase> => {
          const name = "Policy: muted thread → notification suppressed; store entry unaffected";
          try {
            const store = freshStore();
            const event = makeMissedCallEvent(
              "call-c3",
              "thread-c3",
              "alice",
              rosterWithAlice,
            );

            // Log to store regardless of mute
            const logResult = await store.log(event);
            if (!logResult.inserted) {
              return fail(name, "Store log should succeed regardless of mute");
            }

            // Policy should suppress
            const decision = shouldNotify(
              "thread-c3",
              "Alice",
              true, // muted
              Date.now(),
              EMPTY_WINDOW,
            );

            if (decision.tag !== "suppress") {
              return fail(name, `Expected suppress for muted thread, got "${decision.tag}"`);
            }
            if (decision.reason !== "muted") {
              return fail(name, `Expected reason="muted", got "${decision.reason}"`);
            }

            // Store entry is present
            const history = await store.history("thread-c3");
            if (history.length !== 1) {
              return fail(
                name,
                `Store should have 1 entry despite mute; got ${history.length}`,
              );
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // C4: non-roster peer → body is the generic UNKNOWN_CALLER_BODY
        (async (): Promise<TestCase> => {
          const name = "Policy: non-roster caller → notification body is generic";
          try {
            const decision = shouldNotify(
              "thread-c4",
              UNKNOWN_CALLER_HINT, // non-roster
              false,
              Date.now(),
              EMPTY_WINDOW,
            );

            if (decision.tag !== "notify") {
              return fail(
                name,
                `Expected notify, got "${decision.tag}" (reason: ${
                  decision.tag === "suppress" ? decision.reason : "n/a"
                })`,
              );
            }
            if (decision.body !== UNKNOWN_CALLER_BODY) {
              return fail(
                name,
                `Expected body="${UNKNOWN_CALLER_BODY}", got "${decision.body}"`,
              );
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // ── D. Persistence ──────────────────────────────────────────────────

        // D1: write → reopen DB → state preserved
        (async (): Promise<TestCase> => {
          const name = "Persistence: write → reopen DB → history and unread preserved";
          try {
            // Use a stable name for this case so we can "reopen" it
            const dbName = `kaether:missed-calls:persist-test:${Date.now()}`;
            const storeA = new MissedCallStore(dbName);

            const event = makeMissedCallEvent(
              "call-persist",
              "thread-persist",
              "alice",
              rosterWithAlice,
              9000,
            );
            await storeA.log(event);

            // Open a SECOND instance pointing at the same DB name
            const storeB = new MissedCallStore(dbName);
            const history = await storeB.history("thread-persist");
            const unread = await storeB.unreadCount("thread-persist");

            if (history.length !== 1) {
              return fail(
                name,
                `Expected 1 history entry after reopen, got ${history.length}`,
              );
            }
            if (history[0]!.callId !== "call-persist") {
              return fail(
                name,
                `callId mismatch after reopen: "${history[0]!.callId}"`,
              );
            }
            if (history[0]!.callerDisplayHint !== "Alice") {
              return fail(
                name,
                `callerDisplayHint lost after reopen: "${history[0]!.callerDisplayHint}"`,
              );
            }
            if (unread !== 1) {
              return fail(name, `Expected unread=1 after reopen, got ${unread}`);
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),

        // ── E. Rate-limit sliding-window expires ────────────────────────────

        // E1: calls outside the window don't count toward the limit
        (async (): Promise<TestCase> => {
          const name =
            "Policy: calls older than window duration don't count toward rate limit";
          try {
            const baseMs = Date.now();
            let window = EMPTY_WINDOW;

            // Fill ${RATE_LIMIT_MAX} calls that are just outside the 5-min window
            for (let i = 0; i < RATE_LIMIT_MAX; i++) {
              const staleMs = baseMs - RATE_LIMIT_WINDOW_MS - 1_000 - i * 1_000;
              const decision = shouldNotify(
                "thread-e1",
                "Alice",
                false,
                staleMs,
                window,
              );
              if (decision.tag === "notify") {
                window = decision.nextWindow;
              }
            }

            // A fresh call NOW should still be allowed (old calls are pruned)
            const fresh = shouldNotify(
              "thread-e1",
              "Alice",
              false,
              baseMs,
              window,
            );

            if (fresh.tag !== "notify") {
              return fail(
                name,
                `Expected notify after stale calls expired, got "${fresh.tag}"`,
              );
            }
            return pass(name);
          } catch (e) {
            return fail(name, String(e));
          }
        })(),
      ]),
    ),
});
